import React, { useState } from "react";

const TermsAndConditionsPage: React.FC = () => {
  const [openSection, setOpenSection] = useState<number | null>(null);

  const toggleSection = (sectionNumber: number) => {
    setOpenSection((prev) => (prev === sectionNumber ? null : sectionNumber));
  };

  const AccordionSection = ({
    number,
    title,
    children,
  }: {
    number: number;
    title: string;
    children: React.ReactNode;
  }) => (
    <div className="border rounded-lg mb-4 overflow-hidden">
      <button
        className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        onClick={() => toggleSection(number)}
        aria-expanded={openSection === number}
        aria-controls={`section-${number}`}
      >
        <h2 className="text-xl font-semibold text-left">
          {number}. {title}
        </h2>
        <svg
          className={`w-5 h-5 transform transition-transform ${
            openSection === number ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <div
        id={`section-${number}`}
        className={`transition-all duration-300 ease-in-out ${
          openSection === number
            ? "max-h-[5000px] opacity-100"
            : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="p-4 bg-white">{children}</div>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center justify-center">
        <div>Terms and Conditions | Ceylon Hospital</div>
        <meta
          name="description"
          content="Ceylon Hospital's terms and conditions"
        />
      </div>

      <h1 className="mb-6 text-center text-3xl lg:text-6xl font-bold text-gray-700">
        Terms and Conditions
      </h1>
      <p className="mb-4 text-gray-600">
        Last updated: {new Date().toLocaleDateString()}
      </p>

      <div className="prose max-w-none">
        <p className="mb-4">
          These Terms and Conditions ("Terms") govern the use of and access to{" "}
          <a
            href="https://ceylonhospital.com"
            className="text-blue-600 hover:underline"
          >
            https://ceylonhospital.com
          </a>{" "}
          ("Website") and the mobile application CH ("App") (the Website and App
          are collectively referred to as "Platform", which includes all
          content, software, and services available on the Platform). The
          Platform is owned and operated by Indigenous Ceylon Hospital (Private)
          Limited, with its registered office at 796, Aluthmawatha Road, Colombo
          15, Sri Lanka and its affiliates (collectively referred to as "Ceylon
          Hospital", "CH", "we", "us", or "our"). These Terms include our
          privacy policy, available at <a href="https://ceylonhospital.com/privacy-policy" className="text-blue-600 hover:underline">https://ceylonhospital.com/privacy-policy</a> ("Privacy
          Policy"), and any guidelines, additional terms, policies, or
          disclaimers made available by or issued by CH from time to time. We
          reserve the right to offer our users with free trials. These Terms
          would continue to apply to use of such free trials.
        </p>
        <p className="mb-4">
          These Terms constitute a binding and enforceable contract between CH
          and you, an end user of the Platform and the services available
          through the Platform ("you", "user"). You may authorize other persons
          to use the Platform on your behalf in certain cases; in such events,
          you shall be liable for all acts or omissions of the person you
          authorize to use the Platform. If you are using the Platform on behalf
          of another party, you represent and warrant that (a) these Terms have
          been explained in their entirety to you and (b) you have the authority
          to enter into these Terms on behalf of such party and bind such party
          to these Terms. You represent and warrant that you have full legal
          capacity and authority to agree and bind yourself to these Terms.
        </p>
        <p className="mb-4">
          You represent that you are over 18 years of age. If you are below the
          age of 18, you may only use the Platform if your guardian or parent
          has expressly agreed to these Terms on your behalf, and your guardian
          or parent shall be responsible and liable for your obligations arising
          out of these Terms.
        </p>
        <p className="mb-4">
          By using the Platform, you agree that you have read, understood, and
          are bound by these Terms as amended from time to time, and that you
          comply with the requirements listed herein. If you do not agree to all
          of these Terms or comply with the requirements herein, please do not
          access or use the Platform.
        </p>

        <AccordionSection number={1} title="PLATFORM">
          <p className="mb-4">
            <span className="font-semibold">(a)</span> The Platform helps you
            connect with qualified persons specialized in various fields of
            healthcare and related services (for the purposes of these Terms,
            referred to as "Healthcare Service Providers"). The Platform allows
            Users to schedule appointments with Healthcare Service Providers and
            consult them through chat features, videos, and phone calls.
            Healthcare Service Providers may, based on their diagnosis, provide
            Users with medical advice, professional opinions, general advice,
            prescriptions, or other recommendations.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(b)</span> The Platform allows you
            to choose one of the subscription plans we offer ("Subscription
            Plan"). Your use of and access to the Platform is subject to your
            purchase of a Subscription Plan. In addition to Subscription Plans,
            CH may offer you different methods of accessing the Platform. For
            convenience, all methods that allow you to use the Platform –
            including, without limitation, pay per use models – will fall within
            the scope of "Subscription Plans" for the purposes of these Terms.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(c) User Account</span>
          </p>
          <ol className="list-decimal pl-8 mb-4 space-y-2">
            <li>
              You can consult with Healthcare Service Providers on the Platform
              through an account you create, or authorise someone to create on
              your behalf, or through a central corporate account provided by
              your employer ("User Account"). Depending on the type of
              subscription model you have availed, you may have the right to
              create sub-accounts for your family members that are linked to
              your User Account or provide your immediate family members with
              access to your User Account.
            </li>
            <li>
              We might require you to share information, including information
              pertaining to your identification in the process of creation of
              the User Account. You agree that all information provided in this
              regard is complete, true, and accurate. Separately, you should
              ensure the confidentiality of details of the User Account,
              including usernames and passwords. Please note that all personal
              information shared with us shall be treated and processed in
              accordance with our Privacy Policy.
            </li>
            <li>
              You are solely responsible and liable for all activities that take
              place on or through the User Account. We are not liable for any
              unauthorised access to User Accounts, including but not limited to
              hacking and security breaches. We reserve, at our sole discretion,
              the right to suspend a User Account if we are of the opinion that
              the User Account is being accessed by an unauthorised party.
            </li>
          </ol>
          <p className="mb-4">
            <span className="font-semibold">(d)</span> The Company reserves the
            right to manage the hours of when Healthcare Service Providers are
            made available online through the Platform.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(e)</span> Depending on your
            requirements, and subject to the other terms hereunder, you may
            either book an appointment with a specific Healthcare Service
            Provider of your choice on the Platform or choose to have an
            appointment booked with the next available Healthcare Service
            Provider. Where you are choosing your Healthcare Service Provider,
            the Platform will allow you to access details of Healthcare Service
            Providers, including their basic information, qualifications, and
            other users' ratings. This will help you make an informed choice.
            Please note that certain Healthcare Service Providers may require
            you to book an appointment in advance. Further, the Platform may
            allow you to select a language of consultation of your choice. You
            agree and acknowledge that Healthcare Service Providers have the
            sole right to accept or reject your appointment. If a Healthcare
            Service Provider cancels a confirmed appointment, you may be
            entitled to refunds for any fees paid on the Platform as determined
            by CH.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(f)</span> Healthcare Service
            Providers have the right to deny consultations in certain cases.
            This may happen for different reasons, including without limitation,
            if the Healthcare Service Provider is of the opinion that your
            ailment requires a physical consultation to treat you effectively.
            You will not be charged for consultation fees, if applicable, for
            cases covered under this clause.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(g)</span> Subject to the payment of
            a cancellation fee, you are entitled to cancel an appointment with a
            Healthcare Service Provider prior to the commencement of the
            consultation.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(h)</span> If you are unable to
            complete a consultation with a Healthcare Service Provider due to a
            technical failure at your end, and if you cannot connect within two
            minutes from the time the connection was lost, the consultation
            session will automatically terminate. You shall not be entitled to a
            refund in such cases.
          </p>
        </AccordionSection>

        <AccordionSection number={2} title="USER INFORMATION">
          <p className="mb-4">
            <span className="font-semibold">(a)</span> The Platform allows you
            to upload materials and information, including but not limited to
            documents, images, notes, and video and sound recordings ("User
            Information"). You understand that User Information is shared with
            the Healthcare Service Providers you select. Healthcare Service
            Providers may use your User Information to diagnose or otherwise
            advise you.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(b)</span> You hereby grant us a
            non-exclusive, worldwide, perpetual, transferable, sublicensable,
            and royalty-free license to use, display, store, host, communicate,
            make available, modify, adapt, translate, and create derivative
            works of the User Information (i) for the functioning of and in
            connection with the Platform and (ii) in order to share the User
            Information with Healthcare Service Providers that you select
            through the Platform.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(c)</span> You represent and warrant
            that (i) your User Information does not and shall not contain any
            viruses, corrupted data, or other harmful, disruptive, or
            destructive files or content, and (ii) all User Information is true,
            complete, and accurate.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(d)</span> You represent and warrant
            that your User Information is not defamatory, libelous, hateful,
            violent, obscene, pornographic, unlawful, or otherwise offensive.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(e)</span> Our treatment of User
            Information shall be in accordance with our Privacy Policy and
            applicable law.
          </p>
        </AccordionSection>

        <AccordionSection number={3} title="YOUR RESPONSIBILITIES">
          <p className="mb-4">
            <span className="font-semibold">(a)</span> You shall not use the
            Platform in any manner except as expressly permitted in these Terms.
            Without limiting the generality of the preceding sentence, you may
            not:
          </p>
          <ol className="list-decimal pl-8 mb-4 space-y-2">
            <li>
              except as may be provided hereunder, copy, display, distribute,
              modify, publish, reproduce, store, transmit, post, translate,
              create any derivative works of any content made available on the
              Platform, the Platform, or any portion thereof;
            </li>
            <li>
              use the Platform to transmit any data or send or upload any
              material that contains viruses, Trojan horses, worms, time-bombs,
              keystroke loggers, spyware, adware, or any other harmful
              programmes or similar computer code designed to adversely affect
              the operation of any computer software or hardware;
            </li>
            <li>
              use any robot, spider, scrapers, crawlers, avatars, data mining
              tools or the like, other automated devices or processes, or manual
              process to monitor or copy the Platform or any portion thereof;
            </li>
            <li>
              make any back-up or archival copies of the Platform or any part
              thereof;
            </li>
            <li>
              engage in the systematic retrieval of content from the Platform to
              create or compile, directly or indirectly, a collection,
              compilation, database or directory without our prior written
              permission;
            </li>
            <li>
              re-license, sub-license, rent, or lease the rights to use or
              access the Platform or any content or service available on the
              Platform;
            </li>
            <li>
              act in an abusive or threatening manner or use abusive or
              threatening language on or through the Platform;
            </li>
            <li>
              use the Platform in (A) any unlawful manner, (B) for fraudulent or
              malicious activities, or (C) in any manner inconsistent with these
              Terms;
            </li>
            <li>violate applicable laws in any manner;</li>
            <li>
              when using the Platform under a Subscription Plan, use the
              Platform at a frequency that would be deemed to be unreasonable
              and/or higher than a frequency that would be reasonably required
              to receive relevant healthcare. What would be a reasonable level
              in this regard would be determined at our sole discretion and
              would be based on the advice of a Healthcare Service Provider
              consulted by us.
            </li>
          </ol>
          <p className="mb-4">
            <span className="font-semibold">(b)</span> You should not use the
            Platform in an emergency, if you believe you require serious medical
            treatment, you believe your ailments require physical examinations,
            and in the event of accidents.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(c)</span> You shall be solely
            responsible for ensuring compliance with the various applicable
            laws, and you shall be solely liable for any liability that may
            arise due to a breach of your obligations in this regard.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(d)</span> You shall extend all
            cooperation, at your cost, to us in our defence of any proceedings
            that may be initiated against us due to a breach of your obligations
            or covenants under these Terms.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(e)</span> You agree we may contact
            you to develop and share testimonials, anonymous case studies,
            marketing materials, and measurement criteria for the value,
            benefits, and cost savings derived from the Platform.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(f)</span> You agree to receive
            video, audio, and text information from Healthcare Service
            Providers, including for the purposes of conducting both,
            professional and medical consultations. If a Healthcare Service
            Provider cannot complete a video, audio, or text communication with
            you to the Healthcare Service Provider's satisfaction, you agree to
            receive communications over GSM, VoIP, or other services from the
            Healthcare Service Provider in order to continue or complete the
            communication as the case may be.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(g)</span> You agree to receive
            communications, including text messages, push notifications, calls
            and emails, from CH, Healthcare Service Providers, and third parties
            in relation to the Platform, health and wellness or Third Party
            Services.
          </p>
        </AccordionSection>

        <AccordionSection number={4} title="OUR INTELLECTUAL PROPERTY">
          <p className="mb-4">
            <span className="font-semibold">(a)</span> All rights, title, and
            interest in and to the Platform, including all intellectual property
            rights arising out of the Platform, are owned by or otherwise
            licensed to us. Subject to your compliance with these Terms, we
            grant you a non-exclusive, non-transferable, non-sublicensable,
            royalty-free, revocable, and limited licence to use the Platform in
            accordance with these Terms.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(b)</span> You agree that CH may,
            from time to time, use anonymised User Information and other data
            pertaining to your use of the Platform in accordance with our
            Privacy Policy and applicable laws. You hereby agree that all
            intellectual property rights arising out of works created from
            anonymised User Information and other data belongs to and is owned
            by CH. You hereby waive any rights you may have that arise out of
            such works and agree to execute such documentation as CH may request
            in order to give effect to CH's rights under this clause.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(c)</span> We may request you to
            submit suggestions and other feedback, including bug reports,
            relating to the Platform from time to time ("Feedback"). We may
            freely use, copy, disclose, create derivative works on, publish,
            display, distribute, and exploit the Feedback without any payment of
            royalty, acknowledgement, prior consent, or any other form of
            restriction arising out of your intellectual property rights.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(d)</span> Except as stated in these
            Terms, nothing in these Terms should be construed as conferring any
            right in or license to our or any third party's intellectual rights.
          </p>
        </AccordionSection>

        {/* Section 5 */}
        <AccordionSection number={5} title="FEES">
          <p className="mb-4">
            <span className="font-semibold">(a)</span> Depending on the type of
            Subscription Plan you have availed, your use of the Platform may be
            subject to the payment of fees in a mode and manner, and subject to
            terms associated with that payment, prescribed by CH from time to
            time. If a payment is applicable, you shall not be entitled to
            consult with a Healthcare Service Provider on the Platform unless
            your payment method is accepted and verified.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(b) Refunds:</span> You may be
            entitled to refunds, determined by CH at its sole discretion, for
            payments made by you for consultations on a Pay Per Use basis in the
            following events: (i) if a Healthcare Service Provider is unable to
            treat you without a physical examination, (ii) if a Healthcare
            Service Provider decides not to charge you for a consultation on the
            Platform, (iii) if a Healthcare Service Provider cancels a confirmed
            appointment with you and you choose not to consult with another
            available Healthcare Service Provider, or (iv) if a Healthcare
            Service Provider cannot complete a consultation with you due to a
            technical difficulty in relation to the Platform.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(c) Changes:</span> We reserve the
            right to revise our fees from time to time. We shall provide you
            with reasonable advance notice of any changes to our fees. Should
            you not agree to the fees, you have the right to cancel your
            Subscription Plan, provided that all outstanding amounts due to us
            are paid immediately.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(d) Contact:</span> If you have any
            issue or are not satisfied with our service for any reason, you can
            reach out to us at{" "}
            <a
              href="mailto:aswije@ceylonhospital.com"
              className="text-blue-600 hover:underline"
            >
              aswije@ceylonhospital.com
            </a>{" "}
            or by calling{" "}
            <a
              href="tel:+94777307811"
              className="text-blue-600 hover:underline"
            >
              +94 77 730 7811
            </a>{" "}
            (quoting your reference number and reason for the refund request)
            within 24 (Twenty Four) hours from your originally scheduled
            consultation start time and we will look into the issue and arrange
            a refund, if required.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(e) Taxes:</span> The fees are
            exclusive of applicable taxes. These shall be added to our
            invoice(s) at the appropriate rate.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(f) Payment Processors:</span> We
            may use a third-party payment processor ("Payment Processor") to
            bill you through your selected mode of payment. The processing of
            payments will be subject to the terms and policies of such Payment
            Processors in addition to these Terms. We shall not be liable for
            any error by the Payment Processor. In the event of any unsuccessful
            payments, the money so debited shall be credited in accordance with
            the terms of the Payment Processor.
          </p>
        </AccordionSection>

        {/* Section 6 */}
        <AccordionSection number={6} title="THIRD PARTY SERVICES">
          <p className="mb-4">
            <span className="font-semibold">(a)</span> The Platform may include
            services, products, content, documents, and information owned by,
            offered by, or otherwise licensed to a third party ("Third Party
            Services") or contain links to Third Party Services such as health
            tips, blogs, etc. You understand that Third Party Services are the
            responsibility of the third party that created or provided it and
            acknowledge that the use of such Third Party Services is solely at
            your own risk. We make no representations and exclude all warranties
            and liabilities arising out of or pertaining to such Third Party
            Services, including their accuracy, quality, or completeness. We do
            not endorse or sponsor any Third Party Services.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(b)</span> All intellectual property
            rights in and to Third Party Services are the property of the
            respective third parties.
          </p>
        </AccordionSection>

        {/* Section 7 */}
        <AccordionSection number={7} title="TERM AND TERMINATION">
          <p className="mb-4">
            <span className="font-semibold">(a)</span> These Terms shall remain
            in effect unless (i) terminated in accordance with the terms
            hereunder or (ii) until the expiry of the Subscription Plan. You
            agree that your use of the Platform in any manner after the expiry
            or termination of these Terms is solely at your own risk and CH
            shall not be liable for any unauthorised use of the Platform in this
            regard.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(b)</span> We may terminate your
            access to the Platform, or any portion thereof, immediately and at
            any point, without notice, at our sole discretion if you violate or
            breach any of your obligations, responsibilities, or covenants under
            these Terms.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(c)</span> Upon termination or
            expiry of these Terms:
          </p>
          <ol className="list-decimal pl-8 mb-4 space-y-2">
            <li>you shall not be entitled to use the Platform;</li>
            <li>
              you shall not be able to use certain features of the Platform;
            </li>
            <li>your access to the User Account shall time out;</li>
            <li>
              all outstanding amount due to us shall become payable immediately;
              and
            </li>
            <li>
              these Terms shall terminate, except for those paragraphs that
              expressly or are intended to survive termination or expiry.
            </li>
          </ol>
        </AccordionSection>

        <AccordionSection number={8} title="DISCLAIMERS AND WARRANTIES">
          <div id="section-8" className="p-4 bg-white">
            <p className="mb-4">
              <span className="font-semibold">(a)</span> You agree that your use
              of the Platform is at your sole risk. To the extent permitted by
              applicable law, the Platform is provided on an "as is" and "as
              available" basis. We do not warrant that operation of the Platform
              will be uninterrupted or error free or that the functions
              contained in the Platform will meet your requirements.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(b)</span> You agree that your
              engagement with healthcare service providers is at your sole risk.
              CH is an online intermediary that provides a platform to connect
              you to healthcare service providers. It does not bear any
              responsibility for any advice provided or failed to be provided by
              healthcare service providers and disclaims all warranties in
              relation to services provided by healthcare service providers on
              the platform. Your use of the services offered by healthcare
              service providers through the platform does not constitute a
              doctor-patient relationship between CH and you.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(c)</span> You acknowledge and
              agree that CH is a technology platform and is not engaged in the
              provision of medical or healthcare services.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(d)</span> CH is not and will not
              be responsible for any claim or for any damages suffered that are
              related, directly or indirectly, to the provision of services by
              Healthcare Service Providers through the Platform, including but
              not limited to the violation of applicable ethics and laws, the
              violation of standards of care and the duty of care, the provision
              of false or negligent advice or information, violation of third
              party rights, or any abuse, threats, or offensive behavior.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(e)</span> CH is not in the
              business of providing any type of professional medical advice
              whatsoever. We are not liable for any acts, omissions, or any form
              of advice provided by any Healthcare Service Provider through or
              on the Platform. You understand that Healthcare Service Providers
              are not agents or employees of CH; they are independent
              contractors that provide services independently of the Platform.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(f)</span> You acknowledge that
              the Platform is not to be used in a medical emergency or in
              situations requiring critical care.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(g)</span> You understand that
              certain medical conditions require a physical visit with
              Healthcare Service Providers at the location of the Healthcare
              Service Provider. You further understand that if you are either
              unsure about your ability to communicate your condition
              effectively on the Platform, or concerned about the advice
              received on the Platform, or if the advice received on the
              Platform is different to advice you received previously, a
              physical visit with your Healthcare Service Provider is
              recommended. You understand that any advice provided by Healthcare
              Service Providers is based on their experiences, training, and
              medical education. We make no representations about the quality,
              reliability, timing, integrity, authenticity, accuracy, or
              appropriateness of the information provided by the Healthcare
              Service Providers, and assume no responsibility for any of the
              foregoing as more fully set forth below. Without limiting the
              generality of the foregoing, we do not recommend or endorse any
              specific drugs, tests, physicians, products, procedures, opinions,
              "off-label" drug uses, or any other information that may be
              provided through the platform.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(h)</span> You hereby acknowledge
              and agree that Healthcare Service Providers may, depending on the
              type of ailment suffered, choose to not provide you with advice or
              opinions through the Platform. This may occur in different
              situations, including if a physical examination is needed to
              ascertain corresponding advice or treatment.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(i)</span> We do not recommend,
              endorse, or make any representation about the efficacy,
              appropriateness or suitability of any specific tests, procedures,
              treatments, services, opinions, or other information that may be
              contained on or available through the Platform.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(j)</span> CH provides the
              Platform, and Healthcare Service Providers provide services, in
              compliance with laws applicable in Sri Lanka. Healthcare Service
              Providers are not responsible for any differences between such
              rules and any different rules applying to healthcare services in
              any country other than Sri Lanka. If you are accessing the
              Platform outside the territory of Sri Lanka, it is your sole
              responsibility to ensure whether it is lawful to access services
              offered by Healthcare Service Providers in the territory where you
              are located. CH and Healthcare Service Providers shall have no
              liability in this regard.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(k)</span> We do not own, control,
              or endorse any User Information that is transmitted, stored, or
              processed via the Platform. You are solely responsible for the
              User Information. We do not recommend, endorse, or make any
              representation about the efficacy, appropriateness or suitability
              of any User Information that is made available through the
              Platform and disclaim all liabilities or warranties arising out of
              or in connection with any User Information.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(l)</span> CH does not in any
              manner endorse any content published on the Platform by an author,
              or any opinion, recommendation, or advice expressed therein. The
              Company expressly disclaims any and all liability in connection
              with the content. The content is meant for informational purposes
              and the views expressed are those of the author(s) alone. Such
              content is not intended as professional advice for medical
              diagnosis or, as a substitute for the medical advice of a
              physician/doctor. Please note that none of the content should be
              considered free medical advice or a replacement for a
              physician/doctor's appointment.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(m)</span> We do not warrant or
              represent that the Platform will be compatible with any third
              party hardware or software. It shall be your responsibility to
              ensure compatibility of the Platform prior to use. Additionally,
              we shall not be held responsible for any actual, incidental or
              consequential damages that may result from any use or inability to
              use any third-party peripherals with the Platform.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(n)</span> To the fullest extent
              permissible under applicable law, we expressly disclaim all
              warranties of any kind, express or implied, arising out of the
              Platform, including warranties of merchantability, fitness for a
              particular purpose, satisfactory quality, accuracy, title and
              non-infringement, compatibility, applicability, usability,
              appropriateness, and any warranty that may arise out of course of
              performance, course of dealing, or usage.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(o)</span> You hereby accept full
              responsibility for any consequences that may arise from your use
              of the Platform, and expressly agree and acknowledge that we shall
              have absolutely no liability with respect to the same.
            </p>
            <p className="mb-4">
              <span className="font-semibold">(p)</span> To the fullest extent
              permissible by law, we, our affiliates, and our related parties
              each disclaim all liability to you for any loss or damage arising
              out of or due to:
            </p>
            <ol className="list-decimal pl-8 mb-4 space-y-2">
              <li>
                your use of, inability to use, or availability or unavailability
                of the Platform, including any third party services made
                available through the Platform;
              </li>
              <li>
                the occurrence or existence of any defect, interruption,
                deletion of files, delays in the operation or transmission of
                information to, from, or through the Platform, communications
                failure, theft, destruction or unauthorised access to our
                records, programmes, services, server, or other infrastructure
                relating to the Platform;
              </li>
              <li>
                any losses or delays in transmission of messages or material you
                access arising out of the use of any Internet access service
                provider or mobile network service provider or caused by any
                browser or other software which is not under CH's control;
              </li>
              <li>
                the Platform being infected with any malicious code or viruses;
                or
              </li>
              <li>
                the failure of the Platform to remain operational for any period
                of time.
              </li>
            </ol>
            <p className="mb-4">
              <span className="font-semibold">(q)</span> You hereby accept full
              responsibility for any consequences that may arise from your use
              of the Platform and expressly agree and acknowledge that we shall
              have absolutely no liability with respect to the same.
              Notwithstanding anything to the contrary, our maximum aggregate
              liability for any loss shall not exceed the monies received by us
              from you in the three months preceding the date of the claim of
              loss or damage.
            </p>
          </div>
        </AccordionSection>

        <AccordionSection number={9} title="INDEMNITY">
          <p className="mb-4">
            You agree to indemnify and hold harmless us, our affiliates and
            respective directors, officers, members, managers, employees, and
            agents from and against any and all claims and expenses arising out
            of your use of the Platform, a breach of any provision of these
            Terms by you or any person using your account on the Platform, any
            User Information, or any third-party claim to the extent arising
            from or connected with an allegation that your use of the Platform
            in accordance with these Terms infringes any rights of a third
            party.
          </p>
        </AccordionSection>

        {/* Section 10 */}
        <AccordionSection number={10} title="CONSENT TO USE DATA">
          <p className="mb-4">
            <span className="font-semibold">(a)</span> You agree that we may, in
            accordance with our Privacy Policy, collect and use your
            information, health data, and related information.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(b)</span> We may use information
            and data pertaining to your use of the Platform for analytics,
            trends' identification, and purposes of statistics to further
            enhance the effectiveness and efficiency of the Platform and for the
            creation of anonymised dataset and algorithms.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(c)</span> You hereby expressly
            authorise us to disclose, subject to applicable laws, any and all
            information relating to you in our possession to any law enforcement
            or other government officials, if we believe it is necessary or
            appropriate in connection with the investigation or resolution of
            possible crimes, including but not limited to your identity,
            information provided by you, and your correspondence. You further
            understand that we might be directed to disclose any such
            information as may be deemed necessary to satisfy any judicial
            order, law, regulation or valid governmental request.
          </p>
        </AccordionSection>

        {/* Section 11 */}
        <AccordionSection number={11} title="MODIFICATION OF PLATFORM">
          <p className="mb-4">
            We reserve the right at any time to add, modify or discontinue,
            temporarily or permanently, the Platform (or any part thereof) with
            or without notice or cause. You agree and acknowledge that we shall
            not be liable to you or to any third party for any such addition,
            modification, suspension or discontinuation of the Platform.
          </p>
        </AccordionSection>

        {/* Section 12 */}
        <AccordionSection number={12} title="UPDATES">
          <p className="mb-4">
            We reserve the right to provide minor modifications or enhancements,
            or programme temporary fixes or patches, if any, to the Platform
            ("Updates"). You agree that the addition of major functions or
            significant new features to the Platform is not an Update. We shall,
            in our sole discretion, have the right to determine what constitutes
            an Update.
          </p>
        </AccordionSection>

        {/* Section 13 */}
        <AccordionSection number={13} title="FORCE MAJEURE">
          <p className="mb-4">
            We shall have no liability to you if we are prevented from or
            delayed in performing our obligations or from carrying on our
            business, by acts, events, omissions or accidents beyond our
            reasonable control, including, without limitation, strikes,
            lock-outs or other industrial disputes (whether involving our
            workforce or any other party), failure of a utility service or
            telecommunications network, act of God, war, riot, civil commotion,
            malicious damage, compliance with any law or governmental order,
            rule, regulation or direction, accident, breakdown of machinery,
            fire, flood, storm or default of suppliers or sub-contractors.
          </p>
        </AccordionSection>

        {/* Section 14 */}
        <AccordionSection
          number={14}
          title="JURISDICTION, GOVERNING LAWS, AND DISPUTE RESOLUTION"
        >
          <p className="mb-4">
            These Terms shall be governed by, construed and enforced in
            accordance with the laws of Sri Lanka. The courts in Colombo shall
            have exclusive jurisdiction over all issues arising out of these
            Terms or the use of the Platform.
          </p>
        </AccordionSection>

        {/* Section 15 */}
        <AccordionSection number={15} title="MISCELLANEOUS PROVISIONS">
          <p className="mb-4">
            <span className="font-semibold">(a) Modification</span> We reserve
            the right at any time to modify these Terms and to add new or
            additional terms or conditions on your use of the Platform. Such
            modifications and additional terms and conditions will be made
            available on the Platform and, if applicable, will be communicated
            to you. Unless expressly rejected (in which instance these Terms
            shall terminate), such modifications or additions will be effective
            immediately and will be incorporated into these Terms. Your
            continued use of the Platform will be deemed acceptance of such
            modifications and additions.
          </p>
          <p className="mb-4">
            <span className="font-semibold">
              (b) Applicability of Overriding Clauses in Third Party Agreements
            </span>{" "}
            In the instance you are using CH under a Subscription Plan provided
            to you arising out of an agreement/s ("Applicable Sponsor
            Agreement/s") between CH and the party making payments to CH in
            relation to your Subscription Plan ("Sponsor"), these Terms shall be
            subject to any clauses in Applicable Sponsor Agreement/s expressly
            listed as overriding the clauses of these Terms ("Clauses Overriding
            the Standard Terms of Use"). You may request a copy of any Clauses
            Overriding the Standard Terms of Use applicable to you from your
            Sponsor.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(c) Severability</span> If any
            provision of these Terms is determined by any court or other
            competent authority to be unlawful or unenforceable, the other
            provisions of these Terms will continue in effect. If any unlawful
            or unenforceable provision would be lawful or enforceable if part of
            it were deleted, that part will be deemed to be deleted, and the
            rest of the provision will continue in effect (unless that would
            contradict the clear intention of the paragraph, in which case the
            entirety of the relevant provision will be deemed to be deleted).
          </p>
          <p className="mb-4">
            <span className="font-semibold">(d) Notices</span> All notices,
            requests, demands, and determinations for us under these Terms
            (other than routine operational communications) shall be sent to the
            address set forth in our contact page available at{" "}
            <a
              href="https://ceylonhospital/contact/"
              className="text-blue-600 hover:underline"
            >
              https://ceylonhospital/contact/
            </a>
            .
          </p>
          <p className="mb-4">
            <span className="font-semibold">(e) Third Party Rights</span> No
            third party shall have any rights to enforce any terms contained
            herein.
          </p>
          <p className="mb-4">
            <span className="font-semibold">(f) Translations</span> Any
            translated version of these Terms is provided solely for your
            convenience. To the extent any translated version of these Terms
            conflicts with this English version, this English version shall
            prevail.
          </p>
        </AccordionSection>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
